// Cena three.js: ambientes por mapa (céu gradiente, fog, luz, hazard de água/lava,
// estrelas), texturas grid procedurais, materiais por id. Escala UNIT_TO_METERS no
// grupo do mundo — física em units, render em metros.
import * as THREE from 'three';
import { UNIT_TO_METERS, EnvId } from '@surfrun/shared';
import type { BuiltBrush } from '../map/primitives';

interface MatSpec {
  base: string; line: string; accent?: string; rough: number;
  emissive?: string; emissiveIntensity?: number;
}

const MAT_SPECS: Record<string, MatSpec> = {
  floor: { base: '#4a5264', line: '#3d4453', accent: '#5d6880', rough: 0.9 },
  wall: { base: '#343b4a', line: '#2b303d', accent: '#434c60', rough: 0.95 },
  ramp: { base: '#4e6a86', line: '#40586f', accent: '#6d8cab', rough: 0.7 },
  accent: { base: '#38596b', line: '#2e4a5a', accent: '#4d7a8f', rough: 0.8 },
  boost: { base: '#b35c1e', line: '#8f4715', accent: '#e07020', rough: 0.5, emissive: '#ff6a00', emissiveIntensity: 0.35 },
  short: { base: '#5b4a75', line: '#4a3c60', accent: '#7a63a0', rough: 0.7, emissive: '#7a5cff', emissiveIntensity: 0.12 },
  deco: { base: '#4a5266', line: '#3d4453', rough: 0.9 },
  glass: { base: '#88ccee', line: '#79bcdd', rough: 0.2 },
  gold: { base: '#c9a02a', line: '#a8841f', accent: '#e6bc45', rough: 0.35 },
};

interface EnvSpec {
  top: string; mid: string; bot: string;
  fog: string; fogNear: number; fogFar: number;
  hemiSky: string; hemiGround: string; hemiIntensity: number;
  sun: string; sunIntensity: number;
  stars?: boolean;
}

const ENVS: Record<EnvId, EnvSpec> = {
  island: {
    top: '#2f7fd4', mid: '#79b8e8', bot: '#cfe9f5', fog: '#a8d4ea', fogNear: 120, fogFar: 900,
    hemiSky: '#bfe0ff', hemiGround: '#3e6a52', hemiIntensity: 1.0, sun: '#fff3d6', sunIntensity: 1.9,
  },
  dusk: {
    top: '#232c4a', mid: '#4a3a5e', bot: '#8a4f52', fog: '#3a3350', fogNear: 60, fogFar: 700,
    hemiSky: '#9db4d4', hemiGround: '#3a3028', hemiIntensity: 0.85, sun: '#ffd9a8', sunIntensity: 1.7,
  },
  night: {
    top: '#05070f', mid: '#101a33', bot: '#1c2a4d', fog: '#0b1122', fogNear: 80, fogFar: 800,
    hemiSky: '#3c527a', hemiGround: '#141018', hemiIntensity: 0.7, sun: '#9fb6ff', sunIntensity: 1.1, stars: true,
  },
  space: {
    top: '#020208', mid: '#120a24', bot: '#241040', fog: '#0c0618', fogNear: 150, fogFar: 1100,
    hemiSky: '#5a4a8a', hemiGround: '#0a0812', hemiIntensity: 0.65, sun: '#cfd6ff', sunIntensity: 1.2, stars: true,
  },
  lava: {
    top: '#1a0f12', mid: '#48201a', bot: '#8a3a1a', fog: '#301410', fogNear: 60, fogFar: 650,
    hemiSky: '#7a4a3a', hemiGround: '#401a0e', hemiIntensity: 0.8, sun: '#ffb37a', sunIntensity: 1.3,
  },
};

function makeGridTexture(spec: MatSpec): THREE.CanvasTexture {
  const S = 256;
  const cv = document.createElement('canvas');
  cv.width = S; cv.height = S;
  const c = cv.getContext('2d')!;
  c.fillStyle = spec.base;
  c.fillRect(0, 0, S, S);
  const cell = S / 4;
  for (let y = 0; y < 4; y++) {
    for (let x = 0; x < 4; x++) {
      const v = ((x * 7 + y * 13) % 5) - 2;
      c.fillStyle = `rgba(255,255,255,${(v * 0.012).toFixed(3)})`;
      if (v > 0) c.fillRect(x * cell, y * cell, cell, cell);
      c.fillStyle = `rgba(0,0,0,${(-v * 0.012).toFixed(3)})`;
      if (v < 0) c.fillRect(x * cell, y * cell, cell, cell);
    }
  }
  c.strokeStyle = spec.line;
  c.lineWidth = 2;
  for (let i = 0; i <= 4; i++) {
    c.beginPath(); c.moveTo(i * cell + 0.5, 0); c.lineTo(i * cell + 0.5, S); c.stroke();
    c.beginPath(); c.moveTo(0, i * cell + 0.5); c.lineTo(S, i * cell + 0.5); c.stroke();
  }
  c.strokeStyle = spec.accent ?? spec.line;
  c.lineWidth = 4;
  c.strokeRect(1, 1, S - 2, S - 2);
  const tex = new THREE.CanvasTexture(cv);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.anisotropy = 8;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export class GameScene {
  scene = new THREE.Scene();
  world = new THREE.Group(); // escala units→metros
  private matCache = new Map<string, THREE.Material>();
  private edgeMat = new THREE.LineBasicMaterial({ color: 0x7fd4ff, transparent: true, opacity: 0.35 });
  private skyUniforms: { top: { value: THREE.Color }; mid: { value: THREE.Color }; bot: { value: THREE.Color } };
  private hemi: THREE.HemisphereLight;
  private sun: THREE.DirectionalLight;
  private stars: THREE.Points | null = null;
  private hazardMesh: THREE.Mesh | null = null;
  private hazardMat: THREE.MeshStandardMaterial | null = null;
  private time = 0;

  constructor() {
    this.world.scale.setScalar(UNIT_TO_METERS);
    this.scene.add(this.world);

    const skyGeo = new THREE.SphereGeometry(1500, 24, 12);
    this.skyUniforms = {
      top: { value: new THREE.Color('#232c4a') },
      mid: { value: new THREE.Color('#4a3a5e') },
      bot: { value: new THREE.Color('#8a4f52') },
    };
    const skyMat = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      uniforms: this.skyUniforms,
      vertexShader: 'varying vec3 vP; void main(){ vP = position; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }',
      fragmentShader: `varying vec3 vP; uniform vec3 top; uniform vec3 mid; uniform vec3 bot;
        void main(){
          float h = normalize(vP).y;
          vec3 c = h > 0.15 ? mix(mid, top, smoothstep(0.15, 0.7, h)) : mix(bot, mid, smoothstep(-0.25, 0.15, h));
          gl_FragColor = vec4(c, 1.0);
        }`,
    });
    this.scene.add(new THREE.Mesh(skyGeo, skyMat));
    this.scene.fog = new THREE.Fog(0x3a3350, 60, 700);

    this.hemi = new THREE.HemisphereLight(0x9db4d4, 0x3a3028, 0.85);
    this.scene.add(this.hemi);
    this.sun = new THREE.DirectionalLight(0xffd9a8, 1.7);
    this.sun.position.set(60, 120, 40);
    this.scene.add(this.sun);
  }

  /** Aplica o ambiente do mapa (céu, fog, luz, estrelas, hazard visível). */
  setEnv(env: EnvId, hazard: 'water' | 'lava' | 'void' | undefined, killY: number): void {
    const e = ENVS[env];
    this.skyUniforms.top.value.set(e.top);
    this.skyUniforms.mid.value.set(e.mid);
    this.skyUniforms.bot.value.set(e.bot);
    this.scene.fog = new THREE.Fog(new THREE.Color(e.fog), e.fogNear, e.fogFar);
    this.hemi.color.set(e.hemiSky);
    this.hemi.groundColor.set(e.hemiGround);
    this.hemi.intensity = e.hemiIntensity;
    this.sun.color.set(e.sun);
    this.sun.intensity = e.sunIntensity;

    // estrelas
    if (this.stars) { this.stars.removeFromParent(); this.stars.geometry.dispose(); this.stars = null; }
    if (e.stars) {
      const N = 1400;
      const pos = new Float32Array(N * 3);
      for (let i = 0; i < N; i++) {
        const v = new THREE.Vector3().randomDirection().multiplyScalar(1350);
        if (v.y < -100) v.y = -v.y;
        pos.set([v.x, v.y, v.z], i * 3);
      }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      this.stars = new THREE.Points(g, new THREE.PointsMaterial({ color: 0xffffff, size: 2.2, sizeAttenuation: false, fog: false }));
      this.scene.add(this.stars);
    }

    // hazard visível (a morte é o killY; o plano é ~150u acima dele, só visual)
    if (this.hazardMesh) {
      this.hazardMesh.removeFromParent();
      this.hazardMesh.geometry.dispose();
      this.hazardMesh = null;
      this.hazardMat = null;
    }
    if (hazard === 'water' || hazard === 'lava') {
      const mat = new THREE.MeshStandardMaterial(hazard === 'water'
        ? { color: 0x1a6a9e, transparent: true, opacity: 0.85, roughness: 0.15, metalness: 0.1 }
        : { color: 0x501208, emissive: 0xff4a00, emissiveIntensity: 0.9, roughness: 0.7 });
      const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2400, 2400, 1, 1), mat);
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.y = (killY + 150) * UNIT_TO_METERS;
      this.scene.add(mesh);
      this.hazardMesh = mesh;
      this.hazardMat = mat;
    }
  }

  /** Chamar por frame (animações de ambiente). */
  update(dt: number): void {
    this.time += dt;
    if (this.hazardMat) {
      const pulse = Math.sin(this.time * 1.4) * 0.5 + 0.5;
      if (this.hazardMat.emissiveIntensity > 0) {
        this.hazardMat.emissiveIntensity = 0.75 + pulse * 0.35; // lava respira
      } else {
        this.hazardMat.opacity = 0.8 + pulse * 0.08; // água ondula levemente
      }
    }
  }

  private material(id: string): THREE.Material {
    let m = this.matCache.get(id);
    if (!m) {
      const spec = MAT_SPECS[id] ?? MAT_SPECS['floor']!;
      const mat = new THREE.MeshStandardMaterial({
        map: makeGridTexture(spec),
        roughness: spec.rough,
        metalness: 0.05,
      });
      if (spec.emissive) {
        mat.emissive = new THREE.Color(spec.emissive);
        mat.emissiveIntensity = spec.emissiveIntensity ?? 0.3;
        mat.emissiveMap = mat.map;
      }
      if (id === 'glass') { mat.transparent = true; mat.opacity = 0.35; }
      m = mat;
      this.matCache.set(id, m);
    }
    return m;
  }

  addBrushes(built: BuiltBrush[]): void {
    for (const b of built) {
      if (!b.geometry) continue;
      const mesh = new THREE.Mesh(b.geometry, this.material(b.mat));
      this.world.add(mesh);
      const edges = new THREE.EdgesGeometry(b.geometry, 20);
      this.world.add(new THREE.LineSegments(edges, this.edgeMat));
    }
  }

  addZoneMarkers(zones: { kind: string; min: [number, number, number]; max: [number, number, number] }[]): void {
    const colors: Record<string, number> = { start: 0x38d178, end: 0xffd54f, stage: 0x4fc3f7 };
    for (const z of zones) {
      const color = colors[z.kind];
      if (color === undefined) continue;
      const w = z.max[0] - z.min[0], h = z.max[1] - z.min[1], d = z.max[2] - z.min[2];
      const geo = new THREE.BoxGeometry(w, h, d);
      const mesh = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({
        color, transparent: true, opacity: 0.10, depthWrite: false,
      }));
      mesh.position.set(z.min[0] + w / 2, z.min[1] + h / 2, z.min[2] + d / 2);
      this.world.add(mesh);
      const edges = new THREE.LineSegments(
        new THREE.EdgesGeometry(geo),
        new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.6 }),
      );
      edges.position.copy(mesh.position);
      this.world.add(edges);
    }
  }

  clearWorld(): void {
    while (this.world.children.length) {
      const c = this.world.children.pop()!;
      if (c instanceof THREE.Mesh || c instanceof THREE.LineSegments) {
        c.geometry.dispose();
      }
    }
  }
}
